import { Stack, openConfirmModal } from '@inflearn/ds-react-next';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'next-i18next';

import { INFLEARN_URL } from '../../../../constants/urls';
import { useMarketingPartnersCreateMutation } from '../../../../mutations/useMarketingPartnersCreateMutation';
import { getUseClientApiAccountsProfileQueryKeyForAppShell } from '../../../../queries/useClientApiAccountsProfileQuery';
import { PartnersSignupHeader } from './PartnersSignupHeader';
import { PartnersSignupForm } from './form';
import { PARTNERS_TYPE } from './form/constants';
import type { Partners, PartnersResponse } from './form/types';

interface PartnersSignupProps {
  onComplete: (response: PartnersResponse, partners: Partners) => void;
}

export const PartnersSignup = ({ onComplete }: PartnersSignupProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMarketingPartnersCreateMutation({
    options: {
      onSuccess: ({ data }, partners) => {
        onComplete(data, partners);
        /** AppShell 헤더에 profile API를 invalidate해 파트너스인 경우 플로우를 탈 수 있도록 함 */
        queryClient.invalidateQueries(getUseClientApiAccountsProfileQueryKeyForAppShell());
      },
      onError: () => {
        openConfirmModal({
          title: t('가입 실패'),
          children: (
            <>
              {t('가입에 실패했습니다.')}

              <br />
              {t('다시 시도 후 문제가 반복되면 고객 센터로 문의해 주세요.')}
            </>
          ),

          labels: {
            confirm: t('확인'),
            cancel: t('문의하기'),
          },
          cancelProps: {
            onClick: () => {
              window.location.href = INFLEARN_URL.CHANNELTALK();
            },
          },
          footerType: 'right',
          withCloseButton: true,
          size: 'xs',
        });
      },
    },
  });

  const handleSubmit = (partners: Partners) => {
    mutate({
      type: partners.type,
      name: partners.name.trim(),
      email: partners.email.trim(),
      countryCode: partners.countryCode,
      phone: partners.phone.trim(),
      activityLink:
        partners.type === PARTNERS_TYPE.PERSONAL || isEmpty(partners.activityLink?.trim())
          ? undefined
          : partners.activityLink?.trim(),
    });
  };

  return (
    <Stack gap={20}>
      <PartnersSignupHeader />
      <PartnersSignupForm onSubmit={handleSubmit} loading={isLoading} />
    </Stack>
  );
};
